import React, { useState, useEffect } from 'react';
import { 
  getIncompleteSpeakerProfiles, 
  getIncompleteChurchProfiles,
  getIncompleteAttendeeProfiles,
  generateSpeakerReminderEmail,
  generateChurchReminderEmail,
  generateAttendeeReminderEmail,
  wasReminderSentRecently
} from '../../lib/profileReminders';

const AdminRemindersPage = () => {
  const [activeTab, setActiveTab] = useState('speakers');
  const [speakers, setSpeakers] = useState([]);
  const [churches, setChurches] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});
  const [selectedItems, setSelectedItems] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [threshold, setThreshold] = useState(100);
  const [recentReminders, setRecentReminders] = useState({});

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 4000);
  };

  useEffect(() => {
    loadData();
  }, [threshold]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [speakersData, churchesData, attendeesData] = await Promise.all([
        getIncompleteSpeakerProfiles(threshold),
        getIncompleteChurchProfiles(threshold),
        getIncompleteAttendeeProfiles(threshold)
      ]);
      
      setSpeakers(speakersData);
      setChurches(churchesData);
      setAttendees(attendeesData);

      // Check recent reminders for all items
      const recentChecks = {};
      for (const speaker of speakersData) {
        const wasRecent = await wasReminderSentRecently('speaker', speaker.id);
        recentChecks[`speaker_${speaker.id}`] = wasRecent;
      }
      for (const church of churchesData) {
        const wasRecent = await wasReminderSentRecently('church', church.id);
        recentChecks[`church_${church.id}`] = wasRecent;
      }
      for (const attendee of attendeesData) {
        const wasRecent = await wasReminderSentRecently('attendee', attendee.id);
        recentChecks[`attendee_${attendee.id}`] = wasRecent;
      }
      setRecentReminders(recentChecks);
    } catch (error) {
      console.error('Error loading data:', error);
      showToast('Failed to load profiles', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminder = async (type, item) => {
    if (!item.email) {
      showToast(`No email address for ${item.name}`, 'error');
      return;
    }

    const key = `${type}_${item.id}`;
    setSending(prev => ({ ...prev, [key]: true }));

    try {
      const emailContent = type === 'speaker' 
        ? generateSpeakerReminderEmail(item, item.completion)
        : type === 'church'
          ? generateChurchReminderEmail(item, item.completion)
          : generateAttendeeReminderEmail(item, item.completion);

      // Send email via backend function
      const response = await fetch('/.netlify/functions/send-reminder-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipientEmail: item.email,
          recipientName: item.name || (type === 'church' ? item.pastor_name : ''),
          recipientType: type,
          recipientId: item.id,
          subject: emailContent.subject,
          body: emailContent.body,
          missingFields: item.completion.missingFields
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send email');
      }
      
      setRecentReminders(prev => ({ ...prev, [key]: true }));
      showToast(`Reminder sent to ${item.name || item.email}`);
    } catch (error) {
      console.error('Error sending reminder:', error);
      showToast(error.message || 'Failed to send reminder', 'error');
    } finally {
      setSending(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleBulkReminder = async () => {
    const items = activeTab === 'speakers' ? speakers : activeTab === 'churches' ? churches : attendees;
    const type = activeTab === 'speakers' ? 'speaker' : activeTab === 'churches' ? 'church' : 'attendee';
    const selectedData = items.filter(item => selectedItems.includes(item.id));
    
    if (selectedData.length === 0) {
      showToast('Please select items to send reminders', 'error');
      return;
    }

    const withEmail = selectedData.filter(item => item.email);
    if (withEmail.length === 0) {
      showToast('Selected items have no email addresses', 'error');
      return;
    }

    // Send emails individually via backend
    let successCount = 0;
    let failCount = 0;

    for (const item of withEmail) {
      try {
        const emailContent = type === 'speaker' 
          ? generateSpeakerReminderEmail(item, item.completion)
          : type === 'church'
            ? generateChurchReminderEmail(item, item.completion)
            : generateAttendeeReminderEmail(item, item.completion);

        const response = await fetch('/.netlify/functions/send-reminder-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            recipientEmail: item.email,
            recipientName: item.name || (type === 'church' ? item.pastor_name : ''),
            recipientType: type,
            recipientId: item.id,
            subject: emailContent.subject,
            body: emailContent.body,
            missingFields: item.completion.missingFields
          })
        });

        if (response.ok) {
          successCount++;
          const key = `${type}_${item.id}`;
          setRecentReminders(prev => ({ ...prev, [key]: true }));
        } else {
          failCount++;
        }
      } catch (err) {
        failCount++;
        console.error('Error sending to', item.email, err);
      }
    }

    if (failCount === 0) {
      showToast(`Successfully sent ${successCount} reminder${successCount > 1 ? 's' : ''}`);
    } else {
      showToast(`Sent ${successCount}, failed ${failCount}`, failCount > successCount ? 'error' : 'success');
    }
    setSelectedItems([]);
  };

  const toggleSelectAll = () => {
    const items = activeTab === 'speakers' ? speakers : activeTab === 'churches' ? churches : attendees;
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items.map(item => item.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const getCompletionColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400';
    if (percentage >= 50) return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400';
    return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400';
  };

  const currentItems = activeTab === 'speakers' ? speakers : activeTab === 'churches' ? churches : attendees;

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast.show && (
        <div className={`fixed top-20 right-4 z-50 px-4 py-3 rounded-xl shadow-lg ${
          toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white`}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Profile Reminders</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Send reminders to speakers, church admins, and attendees with incomplete profiles
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={threshold}
            onChange={(e) => setThreshold(Number(e.target.value))}
            className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
          >
            <option value={100}>All Incomplete (&lt;100%)</option>
            <option value={80}>Below 80%</option>
            <option value={50}>Below 50%</option>
            <option value={30}>Below 30%</option>
          </select>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{speakers.length}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Incomplete Speakers</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{churches.length}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Incomplete Churches</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{attendees.length}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Incomplete Attendees</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
              <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {speakers.filter(s => s.email).length + churches.filter(c => c.email).length + attendees.filter(a => a.email).length}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">With Email</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {Object.values(recentReminders).filter(Boolean).length}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Reminded (7 days)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => { setActiveTab('speakers'); setSelectedItems([]); }}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'speakers'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
          }`}
        >
          Speakers ({speakers.length})
        </button>
        <button
          onClick={() => { setActiveTab('churches'); setSelectedItems([]); }}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'churches'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
          }`}
        >
          Churches ({churches.length})
        </button>
        <button
          onClick={() => { setActiveTab('attendees'); setSelectedItems([]); }}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'attendees'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
          }`}
        >
          Attendees ({attendees.length})
        </button>
      </div>

      {/* Bulk Actions */}
      {selectedItems.length > 0 && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex items-center justify-between">
          <span className="text-sm font-medium text-primary">
            {selectedItems.length} item{selectedItems.length > 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedItems([])}
              className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900"
            >
              Clear
            </button>
            <button
              onClick={handleBulkReminder}
              className="px-4 py-1.5 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-dark transition-colors"
            >
              Send Bulk Reminder
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : currentItems.length === 0 ? (
          <div className="text-center py-12">
            <svg className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">All profiles complete!</h3>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              No {activeTab} with incomplete profiles below {threshold}%
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedItems.length === currentItems.length && currentItems.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {activeTab === 'speakers' ? 'Speaker' : activeTab === 'churches' ? 'Church' : 'Attendee'}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Completion
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Missing Fields
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {currentItems.map((item) => {
                  const key = `${activeTab === 'speakers' ? 'speaker' : activeTab === 'churches' ? 'church' : 'attendee'}_${item.id}`;
                  const wasRecent = recentReminders[key];
                  
                  return (
                    <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item.id)}
                          onChange={() => toggleSelect(item.id)}
                          className="rounded border-gray-300 text-primary focus:ring-primary"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden flex-shrink-0">
                            {item.photo_url ? (
                              <img src={item.photo_url} alt={item.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-500 dark:text-gray-400 font-medium">
                                {item.name?.charAt(0) || '?'}
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">{item.name || 'Unnamed'}</p>
                            {activeTab === 'speakers' && item.title && (
                              <p className="text-sm text-gray-500 dark:text-gray-400">{item.title}</p>
                            )}
                            {activeTab === 'churches' && item.pastor_name && (
                              <p className="text-sm text-gray-500 dark:text-gray-400">{item.pastor_name}</p>
                            )}
                            {activeTab === 'attendees' && item.email && (
                              <p className="text-sm text-gray-500 dark:text-gray-400">{item.email}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {item.email ? (
                          <span className="text-sm text-gray-600 dark:text-gray-400">{item.email}</span>
                        ) : (
                          <span className="text-sm text-red-500">No email</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${
                                item.completion.percentage >= 80 ? 'bg-green-500' :
                                item.completion.percentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${item.completion.percentage}%` }}
                            />
                          </div>
                          <span className={`text-sm font-medium px-2 py-0.5 rounded ${getCompletionColor(item.completion.percentage)}`}>
                            {item.completion.percentage}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {item.completion.missingFields.slice(0, 3).map((field, idx) => (
                            <span key={idx} className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                              {field}
                            </span>
                          ))}
                          {item.completion.missingFields.length > 3 && (
                            <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                              +{item.completion.missingFields.length - 3} more
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {wasRecent ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Reminded
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 rounded-full">
                            Not reminded
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleSendReminder(activeTab === 'speakers' ? 'speaker' : activeTab === 'churches' ? 'church' : 'attendee', item)}
                          disabled={!item.email || sending[key]}
                          className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                            item.email
                              ? 'bg-primary text-white hover:bg-primary-dark'
                              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          {sending[key] ? (
                            <span className="flex items-center gap-1">
                              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Sending
                            </span>
                          ) : (
                            'Send Reminder'
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminRemindersPage;
